import type { ReactNode } from 'react';
import { LocalizedAnchor } from '@/components/LocalizedLink';

/**
 * Small set of prose primitives shared by every long-form guide body. Kept
 * separate from the docs prose components on purpose: guides render in a
 * marketing/article context (max-w-3xl, larger type) rather than the docs
 * three-column layout. Each guide body is plain JSX built from these.
 */

/** Opening lead paragraph, slightly larger, sets the stage. */
export function Lead({ children }: { children: ReactNode }) {
  return (
    <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
      {children}
    </p>
  );
}

/** A top-level section with an anchor id (for in-page links + GEO extraction). */
export function GuideSection({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section className="mt-12 scroll-mt-28" id={id}>
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 dark:text-white text-balance">
        {title}
      </h2>
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  );
}

export function H3({ children }: { children: ReactNode }) {
  return (
    <h3 className="mt-8 text-xl font-semibold text-gray-900 dark:text-white">{children}</h3>
  );
}

export function P({ children }: { children: ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{children}</p>;
}

export function UL({ children }: { children: ReactNode }) {
  return (
    <ul className="list-disc pl-5 space-y-2 text-gray-600 dark:text-gray-400 leading-relaxed marker:text-gray-400 dark:marker:text-gray-600">
      {children}
    </ul>
  );
}

export function OL({ children }: { children: ReactNode }) {
  return (
    <ol className="list-decimal pl-5 space-y-2 text-gray-600 dark:text-gray-400 leading-relaxed marker:text-gray-400 dark:marker:text-gray-600">
      {children}
    </ol>
  );
}

export function LI({ children }: { children: ReactNode }) {
  return <li className="pl-1">{children}</li>;
}

/** Inline emphasis for a key term. */
export function Term({ children }: { children: ReactNode }) {
  return <strong className="font-semibold text-gray-900 dark:text-white">{children}</strong>;
}

/** Styled internal cross-link (rewrites to .io on ccTLD builds). */
export function GuideLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <LocalizedAnchor
      href={href}
      className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
    >
      {children}
    </LocalizedAnchor>
  );
}

/** A bordered aside: caveats, "not legal advice", key takeaways. */
export function Callout({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="my-6 rounded-xl border border-primary-200/70 dark:border-primary-900/50 bg-primary-50/50 dark:bg-primary-950/20 p-5">
      {title && (
        <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1.5">{title}</p>
      )}
      <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed space-y-2">
        {children}
      </div>
    </div>
  );
}

/** Simple responsive table for comparison / checklist content. */
export function GuideTable({ head, rows }: { head: string[]; rows: ReactNode[][] }) {
  return (
    <div className="my-6 overflow-x-auto rounded-xl border border-gray-200/70 dark:border-gray-800/70">
      <table className="w-full text-sm text-left border-collapse">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900/50">
            {head.map((h) => (
              <th
                key={h}
                className="px-4 py-3 font-semibold text-gray-900 dark:text-white border-b border-gray-200/70 dark:border-gray-800/70"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="align-top">
              {row.map((cell, j) => (
                <td
                  key={j}
                  className="px-4 py-3 text-gray-600 dark:text-gray-400 border-b border-gray-200/50 dark:border-gray-800/50"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
